import { ReactElement, useState, useEffect } from 'react';

import { SolutionSessionMessage } from '@backrs_ui/grow-ui/src/models/models';
import { AcademicMajorValue } from '@backrs_ui/grow-ui/src/pages/Profile/types/profile.types';
import { useGetMajorsByIdsQuery } from '@backrs_ui/grow-ui/src/redux/major/major.api';
import { SchoolApiAutocompleteOption } from '@backrs_ui/grow-ui/src/redux/profile/profile';
import {
  useCreateAcademicHistoryMutation,
  useUpdateAcademicHistoryMutation,
  useGetUserAcademicHistoryQuery,
  UserAcademicHistoryRequest,
} from '@backrs_ui/grow-ui/src/redux/userPortfolio/userAcademicHistory.api';

import {
  prePopulateFormWithAcademicHistory,
  buildAcademicHistoryFromFormValues,
} from './education.utils';
import { EDUCATION_FIELDS, ResumeQuestionField } from './questions.config';
import { ResumeQuestionForm } from './ResumeQuestionForm';
import { FormValue, FormValues } from './types';

const isCurrentEnrollmentField = (fieldKey: string): boolean => {
  return (
    fieldKey === 'academicsHighSchoolCurrentEnrollment' ||
    fieldKey === 'academicsPostSecondaryCurrentEnrollment'
  );
};

type EducationQuestionProps = Readonly<{
  sendQAMessage: (message: string, LLMInput?: string) => void;
  questionNumber?: number;
  message?: SolutionSessionMessage;
}>;

export const EducationQuestion = ({
  sendQAMessage,
  questionNumber = 1,
  message,
}: EducationQuestionProps): ReactElement => {
  const [createAcademicHistory] = useCreateAcademicHistoryMutation();
  const [updateAcademicHistory] = useUpdateAcademicHistoryMutation();
  const [academicHistoryId, setAcademicHistoryId] = useState<number | null>(null);
  const [selectedSchoolName, setSelectedSchoolName] = useState<SchoolApiAutocompleteOption | null>(
    null,
  );
  const [selectedMajorsIds, setSelectedMajorsIds] = useState<number[]>([]);
  const [initialValues, setInitialValues] = useState<FormValues>({});

  const getEducationFields = (questionNumber: number): ResumeQuestionField[] => {
    // the first education question is for high school, thus we filter out degree, major, and major GPA fields
    if (questionNumber === 1) {
      return EDUCATION_FIELDS.filter(
        (field) =>
          ![
            'academicsPostSecondaryCurrentEnrollment',
            'academicDegrees',
            'academicMajors',
            'major_gpa',
          ].includes(field.key),
      );
    }

    return EDUCATION_FIELDS.filter(
      (field) => !['academicsHighSchoolCurrentEnrollment'].includes(field.key),
    );
  };

  const { data: existingAcademicHistory, refetch: refetchAcademicHistory } =
    useGetUserAcademicHistoryQuery(undefined);

  const { data: majorData } = useGetMajorsByIdsQuery(selectedMajorsIds, {
    skip: selectedMajorsIds.length === 0,
  });

  useEffect(() => {
    if (message) {
      refetchAcademicHistory();
    }
  }, [message, refetchAcademicHistory]);

  useEffect(() => {
    if (existingAcademicHistory && existingAcademicHistory.length > 0) {
      const result = prePopulateFormWithAcademicHistory({
        academicHistory: existingAcademicHistory,
        questionNumber,
        formFields: getEducationFields(questionNumber),
      });

      if (result) {
        setAcademicHistoryId(result.academicHistoryId);
        setInitialValues(result.formValues);
        setSelectedSchoolName(result.selectedSchoolName);
        setSelectedMajorsIds(result.selectedMajorsIds);
      }
    }
  }, [existingAcademicHistory, questionNumber]);

  const handleFieldChange = (fieldKey: string, values: FormValue[]) => {
    // update the selected school name when the school field changes
    if (isCurrentEnrollmentField(fieldKey) && values.length > 0) {
      const schoolValue = values[0] as SchoolApiAutocompleteOption;

      // Ensure we properly handle the institution_id - set to null
      const sanitizedSchoolValue = {
        ...schoolValue,
        institution_id: schoolValue?.institution_id || null,
      };

      setSelectedSchoolName(sanitizedSchoolValue);
    }

    // update the selected majors when the majors field changes
    if (fieldKey === 'academicMajors' && values.length > 0) {
      const majorIds = values.map((v) => (v as { academic_majors_id: number }).academic_majors_id);

      setSelectedMajorsIds(majorIds);
    }
  };

  const handleSubmit = async (formValues: FormValues) => {
    // Get institution_id from selected school, allowing null to clear it
    const institutionId = selectedSchoolName?.institution_id ?? null;
    const isUpdate = !!academicHistoryId;

    const academicHistoryData = buildAcademicHistoryFromFormValues({
      formValues,
      formFields: getEducationFields(questionNumber),
      institutionId,
      majorIds: selectedMajorsIds,
      isUpdate,
    });

    if (Object.keys(academicHistoryData).length === 0) return;

    try {
      if (academicHistoryId) {
        await updateAcademicHistory({
          id: academicHistoryId,
          data: academicHistoryData as UserAcademicHistoryRequest,
        }).unwrap();
      } else {
        await createAcademicHistory(academicHistoryData as UserAcademicHistoryRequest).unwrap();
      }
    } catch (error) {
      console.error('Error updating academic history:', error);
    }
  };

  const formatSubmissionData = (formValues: FormValues): FormValues => {
    return {
      ...formValues,
      ...(majorData &&
        formValues.academicMajors && {
          academicMajors: majorData.map((major) => major.name),
        }),
    };
  };

  const getDefaultValue = (
    fieldKey: string,
    formValues: FormValues,
  ): FormValue | FormValue[] | undefined => {
    if (!formValues[fieldKey] || formValues[fieldKey].length === 0) return undefined;

    // Handle current enrollment fields using helper function
    if (isCurrentEnrollmentField(fieldKey)) {
      const schoolValue = formValues[fieldKey][0] as SchoolApiAutocompleteOption;

      if (schoolValue) {
        return {
          ...schoolValue,
          id: schoolValue?.institution_id || undefined,
        };
      }

      return undefined;
    }

    switch (fieldKey) {
      case 'academicMajors':
        if (formValues[fieldKey] && Array.isArray(formValues[fieldKey])) {
          return formValues[fieldKey]
            .filter(
              (value): value is AcademicMajorValue =>
                typeof value === 'object' && value !== null && 'academic_majors_id' in value,
            )
            .map((major) => ({
              id: major.academic_majors_id || major.id,
              major_gpa: major.major_gpa || 0,
            }));
        }

        return formValues[fieldKey];
      case 'academicDegrees': {
        const degreeValue = formValues[fieldKey][0];

        return degreeValue !== undefined && degreeValue !== null
          ? degreeValue.toString()
          : undefined;
      }
      case 'high_school_gpa': {
        const gpaValue = formValues[fieldKey][0];

        if (gpaValue !== undefined && gpaValue !== null && typeof gpaValue === 'object') {
          const objectValues = Object.values(gpaValue);
          const firstValue = objectValues[0];

          return firstValue !== undefined && firstValue !== null
            ? firstValue.toString()
            : undefined;
        }

        return undefined;
      }
      case 'institution_city':
      case 'institution_state': {
        const fieldValue = formValues[fieldKey]?.[0];

        return fieldValue !== undefined && fieldValue !== null ? fieldValue.toString() : undefined;
      }
      default:
        return formValues[fieldKey];
    }
  };

  return (
    <ResumeQuestionForm
      fields={getEducationFields(questionNumber)}
      sendQAMessage={sendQAMessage}
      onFieldChange={handleFieldChange}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      formatSubmissionData={formatSubmissionData}
      getDefaultValue={getDefaultValue}
    />
  );
};
